import axios from 'axios';
import { BpOptionConstantS } from '@modules/BpOption/S/Constant/module/BpOptionConstantS.js';
import { BY_TAG } from '@modules/nfc/S/Constant/nfc';
import { NewMakerEndServiceF } from '../../../NewMaker/EndService/F/Function/module/NewMakerEndServiceF.js';
import {
  getBaseProductInMaker,
  getProductColorInMaker,
  getSelectedOptionGroupInMaker,
} from '../../../Maker/F/getSth';
import { hasNFCOption } from '@modules/Maker/S/product.js';
import { Icon } from '@modules/NewMaker/UI/Icon/Icon.ts';
import { BpOptionGroups, ProductColors } from '../../../../services/database/mco/schema/public';
import { UtilS } from '../../../Util/S/Function/module/UtilS';

interface ExtendedProductColorsSelectedOptionGroup extends ProductColors {
  _: {
    selected_option_group?: {
      _?: any;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

export interface NFCProductParams {
  uri?: string;
  original_url?: string;
}

export class NFCProduct {
  _input_value?: string = undefined;
  _uri?: string = undefined;
  _original_url?: string = undefined;

  constructor(data: NFCProductParams) {
    if (data.uri) this.uri = data?.uri;
    if (data.original_url) this.original_url = data?.original_url;
  }

  get input_value(): string | undefined {
    return this?._input_value;
  }

  set input_value(value: string | undefined) {
    this._input_value = value;
  }

  get uri(): string | undefined {
    return this?._uri;
  }

  set uri(url: string | undefined) {
    this._uri = url;
  }

  get original_url(): string | undefined {
    return this?._original_url;
  }

  set original_url(url: string | undefined) {
    this._original_url = url;
  }

  get nfc_info() {
    return {
      input_value: this.input_value,
      uri: this.uri,
      original_url: this.original_url,
    };
  }

  isNFCOptionGroup(bp_option_group: BpOptionGroups) {
    return (
      bp_option_group.dev_name === 'NFC_URL' &&
      bp_option_group.ui_type_pc === BpOptionConstantS.UI_TYPE.TEXT_INPUT
    );
  }

  createOptionNameToUrl(bp_option_group: BpOptionGroups, escape: (string: string) => string) {
    const url = this.original_url ?? '';
    const icon = new Icon({ name: 'outlink', theme: 'gray-900', class: 'link_icon' }).toHtml();

    const option_name =
      G.collabo_type == ''
        ? escape(
            `<div class="link_option_name" data-url="${url}"><span class="link_text">${TT('nfc_product::check_link')}</span>${icon}</div>`,
          )
        : url;

    return {
      option_group_name: 'URL',
      option_group_name_en: 'URL',
      option_group_name_jp: 'URL',
      option_name,
      // option_name_en: option_name,
      option_name_en: undefined,
      option_name_jp: undefined,
      is_name: bp_option_group.is_name,
      no: bp_option_group.no,
    };
  }

  async insertInfo(selected_option_group_id: number) {
    if (!this.uri) {
      await this.createByTag();
    }

    let isInserted = false;
    let retryCount = 0;
    const maxRetries = 3;

    while (!isInserted && retryCount < maxRetries) {
      try {
        await axios.post('/@api/nfc/info', {
          selected_option_group_id,
          uri: `${BY_TAG}${this.uri}`,
        });
        isInserted = true;
      } catch (error: any) {
        const message = error?.response?.data?.message;
        if (message === 'EXISTING_URI' && retryCount < maxRetries - 1) {
          await this.createByTag();
          retryCount++;
        } else {
          throw error;
        }
      }
    }
  }

  updateNfcInfoToSelectedOptionGroup(product: ExtendedProductColorsSelectedOptionGroup) {
    if (!product._.selected_option_group) return;
    product._.selected_option_group._ = {
      ...product._.selected_option_group._,
      nfc_info: this.nfc_info,
    };
  }

  tempByTag(original_url: string, product?: ExtendedProductColorsSelectedOptionGroup) {
    this.original_url = original_url;
    this.uri = '__optimistic__';
    if (product) {
      this.updateNfcInfoToSelectedOptionGroup(product);
      NewMakerEndServiceF.userProduct.stateChanged();
    }
  }

  async createByTag(original_url = this.original_url, product?: ExtendedProductColorsSelectedOptionGroup) {
    if (!original_url) {
      console.error('original_url이 없습니다.');
      return;
    }

    const prev_original_url = this.original_url;
    const prev_uri = this.uri;

    this.original_url = original_url;
    this.uri = this.uri || '__optimistic__';

    if (product) {
      this.updateNfcInfoToSelectedOptionGroup(product);
      NewMakerEndServiceF.userProduct.stateChanged();
    }

    try {
      const api_create_by_tag =
        G.collabo_type == '' ? `/@api/by_tag` : `/@api/stores/${box.sel('store_id')}/by_tag`;

      const { data } = await axios.post(api_create_by_tag, { original_url: this.original_url });
      this.uri = data.hash;
    } catch (e) {
      this.uri = prev_uri;
      this.original_url = prev_original_url;
    } finally {
      if (product) {
        this.updateNfcInfoToSelectedOptionGroup(product);
        NewMakerEndServiceF.userProduct.stateChanged();
      }
    }
  }

  async updateUrl(original_url = this.original_url, product?: ExtendedProductColorsSelectedOptionGroup) {
    if (!original_url) {
      console.error('original_url이 없습니다.');
      return;
    }

    const prev_original_url = this.original_url;

    this.original_url = original_url;

    if (product) {
      this.updateNfcInfoToSelectedOptionGroup(product);
      NewMakerEndServiceF.userProduct.stateChanged();
    }

    try {
      await axios.put(`/@api/by_tag/hash/${this.uri}`, { original_url });
    } catch (e) {
      this.original_url = prev_original_url;
    } finally {
      if (product) {
        this.updateNfcInfoToSelectedOptionGroup(product);
        NewMakerEndServiceF.userProduct.stateChanged();
      }
    }
  }
}

export const hasNfcInMaker = () => {
  const bp_option_groups = getBaseProductInMaker()._.base_products_bp_option_groups;
  return hasNFCOption(bp_option_groups);
};

export const extractHashFromUri = (uri?: string): string | null => {
  if (!uri || typeof uri !== 'string') return null;

  try {
    // hash 글자수는 7자리
    if (!UtilS.isUrl(uri) && uri.length == 7) return uri;

    const url = new URL(uri);

    const hash = url.pathname.split('/').filter(Boolean)[0];

    return hash || null;
  } catch {
    console.error('hash가 없습니다 ');
    return null;
  }
};

export const initNfcProduct = async () => {
  if (!hasNfcInMaker()) {
    return;
  }

  const nfc_product = getSelectedOptionGroupInMaker()?._?.nfc_info;
  let original_url: string | undefined;
  let uri: string | undefined;

  if (!UtilS.isEmpty(nfc_product)) {
    const hash = extractHashFromUri(nfc_product.uri);

    if (hash) {
      const getByTagApi =
        G.collabo_type == ''
          ? `/@api/by_tag/hash/${hash}`
          : `/@api/stores/${box.sel('store_id')}/by_tag/hash/${hash}`;

      const { data } = await axios.get(getByTagApi);

      original_url = data.url;
      uri = hash;
    }
  }
  const nfcProduct = new NFCProduct({
    original_url,
    uri,
  });

  const product = getProductColorInMaker();
  nfcProduct.updateNfcInfoToSelectedOptionGroup(product);
  return nfcProduct;
};
